import React, { useEffect, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';

import { DefaultTable } from 'src/components';

import { IBenefitsVersionManagement } from 'src/interfaces';

import { DataTableCellEditButton } from 'pg-ui-library';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { monetaryUSDFormat } from 'src/utils/currencyFormat';
import { Container } from './styles';

import { useBenefitsVersion } from './queries/benefits-version.query';

import { useSetBenefitVersion, useSetBenefitVersionEditModal } from '../benefits-version-management';

const customStyles = {
  headRow: {
    style: {
      border: 'none',
    },
  },
  headCells: {
    style: {
      color: '#9399B2',
      font: 'normal normal bold 16px Biotif',
    },
  },
  rows: {
    style: {
      font: 'normal normal normal 14px Biotif',
      color: '#1B1D35',
      borderRadius: '5px',
      padding: '16px 0 16px 0',
      '&:not(:last-of-type)': {
        borderBottomWidth: '0px',
      },
    },
    stripedStyle: {
      color: '#1B1D35',
      backgroundColor: '#F3F4F8',
    },
    highlightOnHoverStyle: {
      backgroundColor: 'rgb(230, 244, 244)',
      borderBottomColor: '#FFFFFF',
      borderRadius: '25px',
      outline: '1px solid #FFFFFF',
    },
  },
  cells: {
    style: {
      justifyContent: 'left',
      width: '150px',
      '&:last-of-type': {
        justifyContent: 'right',
        paddingRight: '36px',
      },
    },
  },
  pagination: {
    style: {
      border: 'none',
    },
  },
};

export const benefitsVersionTableColumns: TableColumn<IBenefitsVersionManagement>[] = [
  {
    name: 'Benefit ID',
    selector: (row: IBenefitsVersionManagement) => row.benefitId || '',
    sortField: 'benefitId',
    width: '180px',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Plan',
    selector: (row: IBenefitsVersionManagement) => row.plan || '',
    sortField: 'Plan',
    width: '180px',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Effective Date',
    selector: (row: IBenefitsVersionManagement) => (row.effectiveDate as string) || '',
    width: '180px',
    sortField: 'effectiveDate',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Expiration',
    selector: (row: IBenefitsVersionManagement) => (row.expiration as string) || '',
    width: '180px',
    sortField: 'expiration',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Rate/Amount',
    selector: (row: IBenefitsVersionManagement) => monetaryUSDFormat(row.amountOrRate) || '',
    width: '180px',
    sortField: 'amountOrRate',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
];

export const BenefitsVersionTable: React.FC<{
  searchTerm: string;
  columns: TableColumn<IBenefitsVersionManagement>[];
}> = ({ searchTerm, columns }) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState('plan');
  const [sortDirection, setSortDirection] = useState('');
  const companyId = useTypedSelector(state => state.config.data?.config?.selectedCompanyId) || '';
  const editButtonWrapper = (onClick: Function) => <DataTableCellEditButton size={30} onClick={() => onClick()} />;
  // const deleteButtonWrapper = (onClick: Function) => <DataTableCellDeleteButton size={30} onClick={() => onClick()} />;

  // const editMutation = useEditBenefitVersion();
  // const deleteMutation = useDeleteBenefitVersion();

  const setIsOpen = useSetBenefitVersionEditModal();
  const setBenefitToEdit = useSetBenefitVersion();

  const columnsAndActions = [
    ...columns,
    {
      // eslint-disable-next-line no-unused-vars
      cell: (row: IBenefitsVersionManagement) =>
        editButtonWrapper(() => {
          setBenefitToEdit(row);
          setIsOpen(true);
        }),
    },
  ];

  const {
    data: benefitsItems,
    refetch,
    isLoading,
    isFetching,
  } = useBenefitsVersion({ search: searchTerm, page, limit, sortBy, sortDirection, companyId });

  useEffect(() => {
    refetch();
  }, [searchTerm, page, limit, sortBy, sortDirection]);

  const handlePageChange = (nPage: number) => {
    setPage(nPage);
  };

  const handlePerRowsChange = async (newLimit: number, nPage: number) => {
    setPage(nPage);
    setLimit(newLimit);
  };

  const handleSort = async (column: TableColumn<IBenefitsVersionManagement>, newSortDirection: SortOrder) => {
    setSortBy(column.sortField as unknown as string);
    setSortDirection(newSortDirection);
  };

  return (
    <Container>
      <DefaultTable
        customStyles={customStyles}
        columns={columnsAndActions}
        data={benefitsItems?.items || []}
        progressPending={isLoading || isFetching}
        pagination
        paginationServer
        paginationTotalRows={benefitsItems?.meta.totalItems || 0}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        onSort={handleSort}
        sortServer
      />
    </Container>
  );
};
